import {StatusResponse, StatusToothCreate, ToothStatus} from "../api-client";

/**
 * Fast chairside tooth-status entry.
 *
 * IMPORTANT — data-model vocabulary (do not blur these):
 *  - `conditionDefinition` (StatusResponse): the reusable clinical condition
 *    (Caries, Fracture, ...). Carries nzisKey / name / nameEn / label. NOT a
 *    patient record.
 *  - `ToothStatus`: the patient+appointment-specific record. Its `id` is what
 *    edit/delete operate on.
 *  - `tooth` (ToothWithRelationships): the tooth/position entity.
 *
 * PRODUCT RULE: the quick-status shortcut list is a FIXED product-level constant
 * (QUICK_STATUS_SHORTCUTS) — the UI always renders these 9 buttons in this exact
 * order regardless of backend data. Each button then *resolves* to a backend
 * conditionDefinition; if it cannot, the button renders disabled (never hidden),
 * so misconfiguration is obvious instead of silently dropping a core shortcut.
 *
 * Resolution prefers STABLE fields, in order:
 *   1. nzisKey exact match
 *   2. an immutable `code`/`canonical` field if the API ever exposes one
 *   3. exact normalized known-name (EN / BG / TR) fallback (never substring)
 *
 * TODO(backend): expose an explicit immutable `code` field on the status API so
 * the frontend can drop the name fallbacks entirely.
 */

export interface QuickStatusShortcut {
    /** Visible button label + keyboard key (uppercase). */
    key: string;
    /** Stable product-level canonical identifier (locale-independent). */
    canonical: string;
    /** Canonical NHIS code matched against StatusResponse.nzisKey (primary). */
    nzisKey: string;
    /** Exact known names (EN/BG/TR) for the last-resort fallback. */
    names: string[];
}

/** The fixed, ordered quick-status shortcut list. Always rendered in this order. */
export const QUICK_STATUS_SHORTCUTS: QuickStatusShortcut[] = [
    {key: 'C', canonical: 'caries', nzisKey: 'C', names: ['Caries', 'Кариес', 'Çürük']},
    {key: 'P', canonical: 'pulpitis', nzisKey: 'P', names: ['Pulpitis', 'Пулпит', 'Pulpitis']},
    {
        key: 'G', canonical: 'periodontitis', nzisKey: 'G',
        names: ['Periapical lesion', 'Periodontitis', 'Периодонтит', 'Periodontit', 'Periapikal lezyon'],
    },
    {key: 'R', canonical: 'root', nzisKey: 'R', names: ['Root', 'Корен', 'Kök']},
    {
        key: 'O', canonical: 'obturation', nzisKey: 'O',
        names: ['Restoration', 'Filling', 'Obturation', 'Обтурация', 'Dolgu', 'Restorasyon'],
    },
    {key: 'E', canonical: 'missing_tooth', nzisKey: 'E', names: ['Missing tooth', 'Липсващ зъб', 'Eksik diş']},
    {key: 'K', canonical: 'crown', nzisKey: 'K', names: ['Crown', 'Корона - самостоятелна', 'Корона', 'Kron']},
    {
        key: 'X', canonical: 'artificial_tooth', nzisKey: 'X',
        names: ['Removable prosthodontic', 'Fixed prosthodontic - pontic',
            'Изкуствен зъб от подвижно протезиране', 'Изкуствен зъб', 'Protez diş', 'Yapay diş'],
    },
    {key: 'F', canonical: 'fracture', nzisKey: 'F', names: ['Fracture', 'Фрактура', 'Kırık', 'Fraktür']},
];

/** The ordered list of supported shortcut letters: C P G R O E K X F. */
export const SHORTCUT_LETTERS: string[] = QUICK_STATUS_SHORTCUTS.map(s => s.key);

const norm = (value?: string | null): string => (value ?? '').trim().toLowerCase();

/**
 * Resolve one shortcut to a backend conditionDefinition (or undefined).
 * Exact stable-key match happens FIRST and is order-independent, so the seven
 * Caries surface variants (label "C", nzisKey Cb/Cl/...) can never suppress the
 * base Caries (nzisKey "C"). No substring matching is ever used.
 */
export const resolveShortcutCondition = (
    shortcut: QuickStatusShortcut,
    conditionDefinitions: StatusResponse[],
): StatusResponse | undefined => {
    if (!conditionDefinitions || conditionDefinitions.length === 0) {
        return undefined;
    }

    // 1. Stable NHIS key — exact, case-insensitive. (Surface variants have
    //    different keys, e.g. Cb/Cl, so they can never beat the base "C".)
    const wantedKey = shortcut.nzisKey.toLowerCase();
    const byKey = conditionDefinitions.find(cd => norm(cd.nzisKey) === wantedKey);
    if (byKey) {
        return byKey;
    }

    // 2. Exact normalized known-name match (EN / BG / TR) on name/nameEn/displayName.
    //    Never substring — "Caries (Buccal)" must not satisfy the base "Caries".
    const wantedNames = new Set(shortcut.names.map(norm));
    const byName = conditionDefinitions.find(cd =>
        wantedNames.has(norm(cd.nameEn)) ||
        wantedNames.has(norm(cd.name)) ||
        wantedNames.has(norm(cd.displayName)),
    );
    if (byName) {
        return byName;
    }

    // 3. Immutable code/canonical field, if the API ever exposes one (forward-compat).
    return conditionDefinitions.find(cd => {
        const code = norm((cd as { code?: string | null }).code);
        return code !== '' && (code === norm(shortcut.canonical) || code === wantedKey);
    });
};

export interface ResolvedQuickStatus {
    key: string;
    canonical: string;
    /** Backend condition, or undefined when the shortcut is not configured. */
    conditionDefinition: StatusResponse | undefined;
}

/**
 * Build the FULL fixed list of 9 shortcuts in canonical order, each annotated
 * with its resolved conditionDefinition (or undefined). Never filters — a
 * missing condition yields a disabled button, not a hidden one.
 */
export const buildQuickStatuses = (conditionDefinitions: StatusResponse[]): ResolvedQuickStatus[] =>
    QUICK_STATUS_SHORTCUTS.map(shortcut => ({
        key: shortcut.key,
        canonical: shortcut.canonical,
        conditionDefinition: resolveShortcutCondition(shortcut, conditionDefinitions),
    }));

/**
 * Resolve a typed/clicked letter (case-insensitive) directly to a
 * conditionDefinition, or undefined if unknown / unconfigured.
 */
export const conditionForLetter = (
    letter: string,
    conditionDefinitions: StatusResponse[],
): StatusResponse | undefined => {
    const shortcut = QUICK_STATUS_SHORTCUTS.find(s => s.key === letter.toUpperCase());
    return shortcut ? resolveShortcutCondition(shortcut, conditionDefinitions) : undefined;
};

/**
 * Normalise a keyboard event key into a supported shortcut letter, or null.
 * Case-insensitive; only single-character keys that map to a shortcut pass.
 */
export const normalizeShortcutKey = (key: string): string | null => {
    if (!key || key.length !== 1) {
        return null;
    }
    const upper = key.toUpperCase();
    return SHORTCUT_LETTERS.includes(upper) ? upper : null;
};

/**
 * Returns true when the active element is a text-entry surface where typing must
 * NOT be hijacked by shortcuts: inputs, textareas, selects, contenteditable
 * (ReactQuill notes), Ant Design select search, and Tagify position editor.
 */
export const isTypingTarget = (el: EventTarget | null): boolean => {
    if (!el || !(el instanceof HTMLElement)) {
        return false;
    }
    const tag = el.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
        return true;
    }
    if (el.isContentEditable) {
        return true;
    }
    return !!el.closest(
        'input, textarea, select, .ql-editor, .tagify, .ant-select, [contenteditable="true"]',
    );
};

/**
 * Pick the authoritative ToothStatus for a tooth from the patient-status list
 * (the SAME source the table edit/delete use). Its `.id` is the real ToothStatus
 * id required by edit + delete — distinct from a condition (StatusResponse) id.
 * Returns the latest record by createdAt, or undefined if the tooth has none.
 */
export const latestToothStatusForTooth = (
    toothStatuses: ToothStatus[],
    toothId?: string | null,
): ToothStatus | undefined => {
    if (!toothId) {
        return undefined;
    }
    return toothStatuses
        // Match on EITHER the scalar FK (`toothId`, always populated) or the nested
        // relation (`tooth.id`). Relying only on the nested object is fragile — if
        // the serializer ever omits/empties `tooth`, the badge silently vanishes.
        .filter(ts => ts.toothId === toothId || ts.tooth?.id === toothId)
        .sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''))
        .slice(-1)[0];
};

export interface ToothRenderStatus {
    /** Patient ToothStatus record (its `.id` is the edit/delete id). */
    existingToothStatus: ToothStatus | undefined;
    /** Resolved reusable condition definition. */
    conditionDefinition: StatusResponse | undefined;
    /** Short badge text (letter). */
    statusLabel: string | undefined;
    /** Header/badge colour. */
    headerColor: string | undefined;
    /** Condition definition id (for active-shortcut highlight). */
    conditionId: string | undefined;
}

/**
 * One lookup used for badge rendering, active shortcut, clear button, and modal
 * edit — so they never disagree. Resolves the tooth's current ToothStatus from
 * `teethStatuses` (same source as table/delete) and its condition definition from
 * `conditionDefinitions`.
 */
export const getToothRenderStatus = (
    tooth: {id?: string | null} | null | undefined,
    teethStatuses: ToothStatus[],
    conditionDefinitions: StatusResponse[],
): ToothRenderStatus => {
    const existingToothStatus = latestToothStatusForTooth(teethStatuses, tooth?.id);
    const conditionDefinition = existingToothStatus
        ? conditionDefinitions.find(cd => cd.id === existingToothStatus.statusId)
        : undefined;
    return {
        existingToothStatus,
        conditionDefinition,
        statusLabel: conditionDefinition?.label
            ?? conditionDefinition?.nzisKey
            ?? existingToothStatus?.statusNameSnapshot
            ?? undefined,
        headerColor: conditionDefinition?.color ?? undefined,
        conditionId: conditionDefinition?.id ?? undefined,
    };
};

/**
 * Build the StatusToothCreate payload for an immediate quick-save of a single
 * condition onto a single tooth. Mirrors the shape produced by the full modal.
 */
export const buildStatusToothCreate = (
    toothId: string | null | undefined,
    conditionDefinition: StatusResponse,
): StatusToothCreate => ({
    toothId: toothId ?? null,
    statusId: conditionDefinition.id,
    nzisStatusId: null,
    notes: '',
});
